import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Types
export interface Coordinate {
  lat: number;
  lon: number;
}

export interface Farm {
  id: string;
  name: string;
  boundary: Coordinate[];
  areaHectares: number;
  cropType: string | null;
  plantingDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IndexResult {
  indexType: string;
  date: string;
  minValue: number;
  maxValue: number;
  meanValue: number;
  stdValue: number;
}

export interface WeatherData {
  date: string;
  temperatureMax: number;
  temperatureMin: number;
  precipitation: number;
  humidity: number;
  windSpeed: number;
}

export interface Alert {
  id: string;
  farmId: string;
  alertType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  detectedAt: string;
  acknowledgedAt: string | null;
}

// Farm API
export const farmApi = {
  list: () => api.get<Farm[]>('/farms').then(r => r.data),
  get: (id: string) => api.get<Farm>(`/farms/${id}`).then(r => r.data),
  create: (data: Partial<Farm>) => api.post<Farm>('/farms', data).then(r => r.data),
  update: (id: string, data: Partial<Farm>) => api.patch<Farm>(`/farms/${id}`, data).then(r => r.data),
  delete: (id: string) => api.delete(`/farms/${id}`),
  getStats: (id: string) => api.get(`/farms/${id}/stats`).then(r => r.data),
};

// Indices API
export const indicesApi = {
  getTimeSeries: (farmId: string, indexType: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get<{ data: IndexResult[] }>(`/indices/farm/${farmId}/${indexType}?${params}`).then(r => r.data);
  },
  getLatest: (farmId: string, indexType: string) =>
    api.get(`/indices/farm/${farmId}/${indexType}/latest`).then(r => r.data),
  getAll: (farmId: string) =>
    api.get(`/indices/farm/${farmId}/all`).then(r => r.data),
};

// Weather API
export const weatherApi = {
  getHistory: (farmId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get<{ data: WeatherData[] }>(`/weather/farm/${farmId}/history?${params}`).then(r => r.data);
  },
  getForecast: (farmId: string, days = 7) =>
    api.get(`/weather/farm/${farmId}/forecast?days=${days}`).then(r => r.data),
  getCurrent: (farmId: string) =>
    api.get(`/weather/farm/${farmId}/current`).then(r => r.data),
};

// Alerts API
export const alertsApi = {
  list: (farmId?: string, severity?: string) => {
    const params = new URLSearchParams();
    if (farmId) params.set('farmId', farmId);
    if (severity) params.set('severity', severity);
    return api.get<{ alerts: Alert[] }>(`/alerts?${params}`).then(r => r.data);
  },
  getByFarm: (farmId: string) =>
    api.get<{ alerts: Alert[] }>(`/alerts/farm/${farmId}`).then(r => r.data),
  acknowledge: (id: string) =>
    api.patch(`/alerts/${id}/acknowledge`).then(r => r.data),
  getSummary: () => api.get('/alerts/summary').then(r => r.data),
};

// Analysis API
export const analysisApi = {
  runAnalysis: (data: { farmId: string; type: string; startDate: string; endDate: string }) =>
    api.post('/analysis/run', data).then(r => r.data),
  getJobStatus: (jobId: string) =>
    api.get(`/analysis/job/${jobId}`).then(r => r.data),
  getClassification: (farmId: string) =>
    api.get(`/analysis/classification/${farmId}`).then(r => r.data),
  getHealth: (farmId: string) =>
    api.get(`/analysis/health/${farmId}`).then(r => r.data),
};

// VRA API
export interface VRAZone {
  zone: string;
  label: string;
  color: string;
  pixelCount: number;
  areaHectares: number;
  indexRange: { min: number; max: number };
  recommendedRate: number;
  unit: string;
}

export interface VRAMap {
  farmId: string;
  mapType: string;
  generatedAt: string;
  zones: VRAZone[];
  statistics: {
    totalArea: number;
    averageRate: number;
    totalProduct: number;
  };
  metadata: {
    cropType: string;
    baseIndex: string;
  };
}

export const vraApi = {
  generate: (data: {
    farmId: string;
    mapType: string;
    cropType: string;
    baseIndex?: string;
    growthStage?: string;
    soilType?: string;
    targetYield?: number;
  }) => api.post<{ success: boolean; data: VRAMap }>('/vra/generate', data).then(r => r.data),
  getByFarm: (farmId: string) =>
    api.get<{ success: boolean; data: { farmId: string; maps: VRAMap[] } }>(`/vra/${farmId}`).then(r => r.data),
  getZoneConfigs: () =>
    api.get<{ success: boolean; data: Record<string, unknown> }>('/vra/config/zones').then(r => r.data),
  getCropRates: () =>
    api.get<{ success: boolean; data: { supportedCrops: string[]; nitrogenRates: Record<string, unknown>; seedingRates: Record<string, unknown> } }>('/vra/config/crops').then(r => r.data),
  exportIsoXml: (data: {
    farmId: string;
    mapType: string;
    cropType: string;
  }) => api.post('/vra/export/iso-xml', data, { responseType: 'blob' }).then(r => r.data),
};

// Disease API
export interface DiseaseInfo {
  id: string;
  name: string;
  scientificName: string;
  affectedCrops: string[];
  symptoms: string[];
  causes: string[];
  treatments: {
    chemical: string[];
    biological: string[];
    cultural: string[];
    organic: string[];
  };
  preventionMeasures: string[];
  optimalConditions: {
    temperature: { min: number; max: number };
    humidity: { min: number; max: number };
  };
}

export interface DiseaseRisk {
  diseaseId: string;
  diseaseName: string;
  riskLevel: 'low' | 'moderate' | 'high' | 'very_high';
  riskScore: number;
  riskColor: string;
  contributingFactors: string[];
  recommendations: string[];
}

export interface DiseaseForecast {
  farmId: string;
  cropType: string;
  forecastPeriod: { start: string; end: string };
  overallRisk: string;
  dailyRisks: Array<{
    date: string;
    diseases: DiseaseRisk[];
  }>;
}

export const diseaseApi = {
  detect: (data: { imageBase64: string; cropType: string; farmId?: string }) =>
    api.post<{ success: boolean; data: { detections: unknown[]; cropType: string; analyzedAt: string } }>('/diseases/detect', data).then(r => r.data),
  forecast: (data: {
    farmId: string;
    cropType: string;
    growthStage?: string;
    weatherForecast: Array<{
      date: string;
      temperatureMin: number;
      temperatureMax: number;
      temperatureMean: number;
      humidity: number;
      precipitation: number;
    }>;
  }) => api.post<{ success: boolean; data: DiseaseForecast }>('/diseases/forecast', data).then(r => r.data),
  getInfo: (diseaseId: string) =>
    api.get<{ success: boolean; data: DiseaseInfo }>(`/diseases/info/${diseaseId}`).then(r => r.data),
  getByCrop: (cropType: string) =>
    api.get<{ success: boolean; data: { cropType: string; diseases: DiseaseInfo[]; count: number } }>(`/diseases/crop/${cropType}`).then(r => r.data),
  getAll: () =>
    api.get<{ success: boolean; data: { diseases: DiseaseInfo[]; count: number } }>('/diseases/all').then(r => r.data),
  searchBySymptoms: (symptoms: string[]) =>
    api.post<{ success: boolean; data: { diseases: DiseaseInfo[]; count: number } }>('/diseases/search', { symptoms }).then(r => r.data),
  getRiskLevels: () =>
    api.get<{ success: boolean; data: Record<string, { color: string; description: string }> }>('/diseases/risk-levels').then(r => r.data),
};

// Irrigation API
export interface IrrigationDay {
  date: string;
  et0: number;
  etc: number;
  precipitation: number;
  irrigationNeeded: number;
  irrigationRecommended: boolean;
  soilMoistureStart: number;
  soilMoistureEnd: number;
  stressLevel: 'none' | 'mild' | 'moderate' | 'severe';
  stressColor: string;
}

export interface IrrigationSchedule {
  farmId: string;
  cropType: string;
  soilType: string;
  generatedAt: string;
  summary: {
    totalIrrigationNeeded: number;
    totalPrecipitation: number;
    daysRequiringIrrigation: number;
    averageET0: number;
    averageETc: number;
  };
  schedule: IrrigationDay[];
}

export const irrigationApi = {
  generateSchedule: (data: {
    farmId: string;
    cropType: string;
    soilType?: string;
    daysAfterPlanting: number;
    location: { latitude: number; elevation?: number };
    weatherForecast: Array<{
      date: string;
      temperatureMax: number;
      temperatureMin: number;
      humidity: number;
      windSpeed?: number;
      precipitation?: number;
      solarRadiation?: number;
    }>;
    options?: {
      irrigationSystem?: string;
      currentSoilMoisture?: number;
      rootDepth?: number;
    };
  }) => api.post<{ success: boolean; data: IrrigationSchedule }>('/irrigation/schedule', data).then(r => r.data),
  calculateET0: (data: {
    date: string;
    temperatureMax: number;
    temperatureMin: number;
    humidity: number;
    windSpeed: number;
    latitude: number;
    elevation?: number;
    solarRadiation?: number;
  }) => api.post<{ success: boolean; data: { et0: number; details: unknown } }>('/irrigation/et0', data).then(r => r.data),
  calculateETc: (data: { et0: number; cropType: string; daysAfterPlanting: number }) =>
    api.post<{ success: boolean; data: { etc: number; kc: number; growthStage: string } }>('/irrigation/etc', data).then(r => r.data),
  getQuickRecommendation: (data: {
    et0: number;
    precipitation: number;
    soilMoisturePercent: number;
    cropType: string;
    daysAfterPlanting: number;
  }) => api.post<{ success: boolean; data: unknown }>('/irrigation/quick-recommendation', data).then(r => r.data),
  getSoilMoistureFromNDMI: (ndmi: number) =>
    api.get<{ success: boolean; data: { ndmi: number; estimatedMoisture: number; category: string } }>(`/irrigation/soil-moisture/from-ndmi/${ndmi}`).then(r => r.data),
  getCropCoefficients: (cropType: string) =>
    api.get<{ success: boolean; data: { cropType: string; coefficients: unknown; seasonLength: number } }>(`/irrigation/crop-coefficients/${cropType}`).then(r => r.data),
  getSupportedCrops: () =>
    api.get<{ success: boolean; data: { crops: string[] } }>('/irrigation/config/crops').then(r => r.data),
  getSupportedSoils: () =>
    api.get<{ success: boolean; data: { soilTypes: string[]; properties: unknown } }>('/irrigation/config/soils').then(r => r.data),
  getSupportedSystems: () =>
    api.get<{ success: boolean; data: { systems: string[]; specifications: unknown } }>('/irrigation/config/systems').then(r => r.data),
};

// Crop Calendar API
export interface GrowthStage {
  name: string;
  startDay: number;
  duration: number;
  description: string;
  keyIndicators: string[];
  waterNeeds: string;
  nutrientNeeds: string;
}

export interface CalendarActivity {
  id: string;
  name: string;
  category: string;
  startDay: number;
  endDay: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  tips: string[];
  weatherConsiderations: string[];
}

export interface CropCalendar {
  cropType: string;
  totalDuration: number;
  stages: GrowthStage[];
  activities: CalendarActivity[];
}

export interface FarmCalendar {
  farmId: string;
  cropType: string;
  plantingDate: string;
  expectedHarvestDate: string;
  currentDay: number;
  currentStage: GrowthStage;
  upcomingActivities: Array<CalendarActivity & { scheduledDate: string; daysUntil: number }>;
  completedActivities: string[];
  progress: number;
}

export const calendarApi = {
  getAvailableCrops: () =>
    api.get<{ success: boolean; data: { availableCrops: string[]; count: number } }>('/calendar/crops').then(r => r.data),
  getTemplate: (cropType: string) =>
    api.get<{ success: boolean; data: CropCalendar }>(`/calendar/template/${cropType}`).then(r => r.data),
  generateFarmCalendar: (data: {
    farmId: string;
    cropType: string;
    plantingDate: string;
    completedActivityIds?: string[];
  }) => api.post<{ success: boolean; data: FarmCalendar }>('/calendar/farm', data).then(r => r.data),
  getCurrentStage: (cropType: string, daysAfterPlanting: number) =>
    api.get<{ success: boolean; data: { cropType: string; daysAfterPlanting: number; currentStage: GrowthStage; nextStage: GrowthStage | null; daysToNextStage: number; stageProgress: number } }>(`/calendar/stage/${cropType}/${daysAfterPlanting}`).then(r => r.data),
  getUpcomingActivities: (cropType: string, daysAfterPlanting: number, daysAhead = 14) =>
    api.get<{ success: boolean; data: { activities: CalendarActivity[]; count: number } }>(`/calendar/activities/upcoming/${cropType}/${daysAfterPlanting}?daysAhead=${daysAhead}`).then(r => r.data),
  getOverdueActivities: (cropType: string, daysAfterPlanting: number) =>
    api.get<{ success: boolean; data: { overdueActivities: CalendarActivity[]; count: number } }>(`/calendar/activities/overdue/${cropType}/${daysAfterPlanting}`).then(r => r.data),
  getRecommendations: (data: {
    cropType: string;
    daysAfterPlanting: number;
    weatherConditions?: { precipitation: number; temperature: number };
  }) => api.post<{ success: boolean; data: { recommendations: unknown[] } }>('/calendar/recommendations', data).then(r => r.data),
  getAllStages: (cropType: string) =>
    api.get<{ success: boolean; data: { cropType: string; totalDuration: number; stages: GrowthStage[] } }>(`/calendar/stages/${cropType}`).then(r => r.data),
  getAllActivities: (cropType: string, category?: string, priority?: string) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (priority) params.set('priority', priority);
    return api.get<{ success: boolean; data: { cropType: string; activities: CalendarActivity[]; count: number } }>(`/calendar/activities/${cropType}?${params}`).then(r => r.data);
  },
  getDaysAfterPlanting: (plantingDate: string, targetDate?: string) => {
    const params = new URLSearchParams({ plantingDate });
    if (targetDate) params.set('targetDate', targetDate);
    return api.get<{ success: boolean; data: { plantingDate: string; targetDate: string; daysAfterPlanting: number } }>(`/calendar/days-after-planting?${params}`).then(r => r.data);
  },
};

// Health check
export const healthCheck = () =>
  axios.get(API_URL.replace('/api/v1', '/')).then(r => r.data);

export default api;
